import { Injectable, inject } from '@angular/core';
import { Feature } from '@turf/turf';
import * as _ from 'lodash';
import { asyncScheduler, BehaviorSubject, Observable, Subject } from 'rxjs';
import { delay, distinctUntilChanged, map, throttleTime } from 'rxjs';
import { Vehicle, VehicleWarning, VehicleWarningConfig, WarningMessageType } from '../../shared/models/vehicle';
import { SelectedVehiclesDictionary } from '../models/custom-dictionary';
import { AppStateService } from '../services/app-state.service';
import { Store } from './store.model';

export interface VehicleStoreState {
    vehicles: Map<string, Vehicle>;
    selectedVehicles: SelectedVehiclesDictionary;
}

export interface VehicleWarningByType {
    warningType: WarningMessageType;
    totalWarnings: number;
    warningsText: string[];
}

export const getVehicle = (state: VehicleStoreState, inventoryNumber: string): Vehicle => {
    if (inventoryNumber) {
        return state?.vehicles?.get(inventoryNumber);
    } else {
        return undefined;
    }
};

@Injectable({
    providedIn: 'root'
})
export class VehicleStore extends Store<VehicleStoreState> {
    private _appState = inject(AppStateService);

    public processedFeatures$: Observable<Feature[]>;
    public processedWarnings$: Observable<VehicleWarningByType[]>;

    private _processedFeatures$ = new BehaviorSubject<Feature[]>([]);
    private _processedWarnings$ = new BehaviorSubject<VehicleWarningByType[]>([]);
    private _inventoryFilter: string[] = [];

    private _triggerRemoveGarageCode$: Subject<string> = new Subject();
    private _removeGarageCode$ = this._triggerRemoveGarageCode$.asObservable();

    private _disabledWarningTypes: WarningMessageType[];
    private _warningConfig: VehicleWarningConfig;


    constructor() {
        super({
            vehicles: undefined,
            selectedVehicles: undefined
        });

        this.processedFeatures$ = this._processedFeatures$.asObservable();
        this.processedWarnings$ = this._processedWarnings$.asObservable();

        this.getVehiclesState().subscribe(() => {
            // send features
            this._sendFeatures();
            this._sendWarnings();
        });

        this._appState.getPropertyState('selectedContext')
            .subscribe(() => {
                this._sendFeatures();
            });

        this._removeGarageCode$
            .pipe(
                delay(1000)
            )
            .subscribe((garageCode: string) => {
                if (this.stateSnapshot.vehicles) {
                    this.stateSnapshot.vehicles.forEach((vehicle: Vehicle, inventoryNumber: string) => {
                        if (vehicle.garageCode === garageCode) {
                            this.stateSnapshot.vehicles.delete(inventoryNumber);
                        }
                    });
                }

                this.setState({
                    ...this.stateSnapshot
                });
            });

        this._appState.getPropertyState('warningConfig')
            .subscribe((warningConfig: VehicleWarningConfig) => {
                this._warningConfig = warningConfig;
                this._sendFeatures();
                this._sendWarnings();
            });
    }

    public getVehicleState(inventoryNumber: string) {
        return super.state$.pipe(
            map(state => getVehicle(state, inventoryNumber)),
            distinctUntilChanged()
        );
    }

    public getVehiclePropertyState<K extends keyof Vehicle>(inventoryNumber: string, propertyKey: K) {
        return super.state$.pipe(
            map(state => getVehicle(state, inventoryNumber)),
            map(vehicle => vehicle && vehicle[propertyKey]),
            distinctUntilChanged()
        );
    }

    public getVehiclesState(): Observable<Vehicle[]> {
        return super.state$.pipe(
            map(state => state.vehicles !== undefined ? Array.from(state.vehicles.values()) : null),
            throttleTime(500, asyncScheduler, { trailing: true })
        );
    }

    public getSelectedVehiclesState(): Observable<SelectedVehiclesDictionary> {
        return super.state$.pipe(
            map(state => state.selectedVehicles),
            distinctUntilChanged()
        );
    }

    public update(vehiclesToUpdate: Vehicle[]) {
        if (!vehiclesToUpdate?.length) {
            return;
        }

        let vehicles = super.stateSnapshot.vehicles;

        if (!vehicles) {
            vehicles = new Map<string, Vehicle>();
        }

        _.each(vehiclesToUpdate, (vehicle) => {
            const existingVehicle: Vehicle = this.stateSnapshot.vehicles?.get(vehicle.inventoryNumber);

            if (existingVehicle !== undefined) {
                if (vehicle.position) {
                    if (existingVehicle.position) {
                        if (vehicle.position.timestamp && existingVehicle.position.timestamp > vehicle.position.timestamp) {
                            vehicle.position = existingVehicle.position;
                        }
                    }
                }

                // Deviation timestamp guard — drop incoming Arrival/Departure events
                // older than what we already have. Mirrors the position guard above.
                // Without this, an out-of-order event can rewind state and place the
                // vehicle on a stale stop in line-view.
                const incomingDev = vehicle.arrivalDeviation ?? vehicle.departureDeviation;
                if (incomingDev?.measuredAt) {
                    const toMs = (d: unknown): number => {
                        if (!d) return NaN;
                        if (d instanceof Date) return d.getTime();
                        if (typeof d === 'string') return Date.parse(d);
                        return NaN;
                    };
                    const incomingMs = toMs(incomingDev.measuredAt);
                    const existingMsList = [
                        toMs(existingVehicle.arrivalDeviation?.measuredAt),
                        toMs(existingVehicle.departureDeviation?.measuredAt),
                    ].filter(v => !isNaN(v));
                    if (!isNaN(incomingMs) && existingMsList.length > 0
                        && incomingMs < Math.max(...existingMsList)) {
                        delete vehicle.arrivalDeviation;
                        delete vehicle.departureDeviation;
                        delete vehicle.progressType;
                    }
                }

                if (vehicle.progressType === 'Arrival') {
                    delete vehicle.departureDeviation;
                } else if (vehicle.progressType === 'Departure') {
                    delete vehicle.arrivalDeviation;
                }

                if (vehicle.warnings?.length > 0 || existingVehicle.warnings?.length > 0) {
                    // Incomming warnings can be isActive = false, so we need to remove these from existing in that case
                    const incommingWarnings = vehicle.warnings?.slice() || [];

                    // Existing warnings should always have isActive = true
                    const existingWarnings = existingVehicle.warnings?.slice() || [];

                    // Set markedAsStale to false for incoming warnings that already exist
                    for (const incomingWarning of incommingWarnings) {
                        const existsInStore = existingWarnings.find(
                            (w) => w.id === incomingWarning.id
                        );
                        if (existsInStore) {
                            incomingWarning.markedAsStale = false;
                        }
                    }

                    // Will contain the warnings to add to the vehicle from the existing warnings
                    const warningsToAdd = [];

                    // Check if we should remove any exising warning and overwrite it by the existing warning
                    for (const warning of existingWarnings) {
                        const existsInIncomming = _.findIndex(incommingWarnings, w => w.id === warning.id) !== -1;

                        //sometimes the backend sends several signonWarnings for same route/trip but with different sequenceNumbers
                        //this is probably due to a bug in backend 
                        //we filter them out here, since they confuse the user, and one signOnWarning is enough 
                        const isSuperseded = warning.messageType === WarningMessageType.SignOnMissingWarning &&
                            _.findIndex(incommingWarnings, w =>
                                w.messageType === WarningMessageType.SignOnMissingWarning &&
                                w.metadata?.routeName === warning.metadata?.routeName &&
                                w.metadata?.tripNumber === warning.metadata?.tripNumber &&
                                w.metadata?.trafficDate === warning.metadata?.trafficDate &&
                                w.id !== warning.id
                            ) !== -1;

                        if (!existsInIncomming && !isSuperseded) {
                            warningsToAdd.push(warning);
                        }
                    }

                    vehicle.warnings = _.chain(warningsToAdd)
                        .concat(incommingWarnings)
                        .filter((w: VehicleWarning) => w.isActive)
                        .filter((w: VehicleWarning) => {
                            // Filter out expired SignOnMissing warnings (where vehicle has moved to a different trip)
                            if (w.messageType === WarningMessageType.SignOnMissingWarning) {
                                const warningRoute = w.metadata?.routeName;
                                const warningTrip = w.metadata?.tripNumber;
                                const currentRoute = vehicle.routeNumber || existingVehicle?.routeNumber;
                                const currentTrip = vehicle.tripNumber || existingVehicle?.tripNumber;

                                // If vehicle has current trip data, check if warning matches
                                if (currentRoute && currentTrip) {
                                    return warningRoute === currentRoute && warningTrip === currentTrip;
                                }

                                //no currentRoute or currentTrip
                                //but explicetely not on trip, warning is stale
                                if (existingVehicle.onTomLayOvr) {
                                    return false;
                                }

                                //not pullover. maybe we just have not gotten the trip info
                                //do not filter here
                                return true;
                            }
                            return true;
                        })
                        .orderBy('severityRatio', 'desc')
                        .value();
                }

                if (vehicle.confirmedWarnings?.length > 0 || existingVehicle.confirmedWarnings?.length > 0) {
                    const confirmedNewWarnings = vehicle.confirmedWarnings?.slice() || [];
                    const confirmedExistingWarnings = existingVehicle.confirmedWarnings?.slice() || [];
                    
                    // Set markedAsStale to false for incoming confirmed warnings that already exist
                    for (const incomingConfirmed of confirmedNewWarnings) {
                        const existsInStore = confirmedExistingWarnings.find((w) => w.id === incomingConfirmed.id);

                        if (existsInStore) {
                            incomingConfirmed.markedAsStale = false;
                        }
                    }
                    
                    // Add only existing confirmed warnings that don't exist in incoming (to avoid duplicates)
                    const confirmedWarningsToAdd = [];
                    for (const confirmedWarning of confirmedExistingWarnings) {
                        const existsInIncoming = _.findIndex(confirmedNewWarnings, w => w.id === confirmedWarning.id) !== -1;
                        if (!existsInIncoming) {
                            confirmedWarningsToAdd.push(confirmedWarning);
                        }
                    }
                    
                    vehicle.confirmedWarnings = confirmedNewWarnings.concat(confirmedWarningsToAdd);
                }

                this._setDepartureDeviationProperties(vehicle);
                
                // Preserve currentAssignment if incoming vehicle doesn't have it
                if (!vehicle.currentAssignment && existingVehicle.currentAssignment) {
                    vehicle.currentAssignment = existingVehicle.currentAssignment;
                }
                
                Object.assign(existingVehicle, vehicle);

                if (existingVehicle.onTomLayOvr) {
                    // should not have any deviation data
                    delete existingVehicle.departureDeviation;
                    delete existingVehicle.arrivalDeviation;
                }
            } else {
                // should not have any deviation data
                if (vehicle.onTomLayOvr) {
                    delete vehicle.departureDeviation;
                    delete vehicle.arrivalDeviation;
                }

                this._setDepartureDeviationProperties(vehicle);

                // Sort by severityRatio and remove inactive warnings.
                vehicle.warnings = _.orderBy(_.filter(vehicle.warnings, 'isActive'), 'severityRatio', 'desc');  

                vehicles.set(vehicle.inventoryNumber, vehicle);
            }
        });

        this.setState({
            ...this.stateSnapshot,
            vehicles,
        });
    }

    public get(inventoryNumber: string) {
        this.setState({
            ...this.stateSnapshot
        });
    }

    public remove(garageCode: string) {
        this._triggerRemoveGarageCode$.next(garageCode);
    }

    public setInventoryFilter(inventoryNumbers: string[] | undefined): void {
        this._inventoryFilter = inventoryNumbers;
        this._sendFeatures();
    }

    public markAllWarningsAsStale(): void {
        const vehicles = this.stateSnapshot.vehicles;

        if (!vehicles) {
            return;
        }

        // Clear warnings from all vehicles
        vehicles.forEach((vehicle: Vehicle) => {
            if (vehicle.warnings) {
                vehicle.warnings.forEach(warning => warning.markedAsStale = true);
            }
            
            if (vehicle.confirmedWarnings) {
                vehicle.confirmedWarnings.forEach(warning => warning.markedAsStale = true);
            }
        });

        // Update state to trigger subscribers
        this.setState({
            ...this.stateSnapshot,
            vehicles,
        });
    }

    public clearStaleWarnings(): void {
        const vehicles = this.stateSnapshot.vehicles;

        if (!vehicles) {
            return;
        }

        // Remove warnings that are still marked as stale
        vehicles.forEach((vehicle: Vehicle) => {
            if (vehicle.warnings) {
                vehicle.warnings = vehicle.warnings.filter(warning => !warning.markedAsStale);
            }
            
            if (vehicle.confirmedWarnings) {
                vehicle.confirmedWarnings = vehicle.confirmedWarnings.filter(warning => !warning.markedAsStale);
            }
        });

        // Update state to trigger subscribers
        this.setState({
            ...this.stateSnapshot,
            vehicles,
        });

        // Refresh processed warnings display
        this._sendWarnings();
    }

    //#region Selection of vehicles
    public selectVehicles(ids: string[], source: string) {
        let current = this.stateSnapshot?.selectedVehicles;
        if (!current) {
            current = new SelectedVehiclesDictionary();
        }
        current.addOrReplace(source, ids);
        this.setState({
            ...this.stateSnapshot,
            selectedVehicles: current
        });
    }

    public resetVehiclesSelection(source: string) {
        let current = this.stateSnapshot?.selectedVehicles;
        if (!current) {
            current = new SelectedVehiclesDictionary();
        }
        current.addOrReplace(source, []);
        this.setState({
            ...this.stateSnapshot,
            selectedVehicles: current
        });
    }
    //#endregion

    private _sendWarnings() {
        let vehicles: Vehicle[];
        if (!super.stateSnapshot.vehicles) {
            vehicles = [];
        } else {
            vehicles = Array.from(super.stateSnapshot.vehicles.values());
        }

        const processedVehicles = _.chain(vehicles)
            .filter(vehicle => vehicle.warnings.length > 0)
            .map(v => _.map(v.displayableWarnings, w => ({
                warning: w,
                inventoryNumberWithoutPrefix: v.inventoryNumberWithoutPrefix
            })))
            .flatten()
            .value();

        const visibleWarningTypes = _.pullAll(this._warningConfig?.getWarningsOrder(), this._disabledWarningTypes);

        const result = [];
        // For every warning message type that exists, collect information
        _.each(visibleWarningTypes, vmType => {
            const warningsByType = _.filter(processedVehicles, (warning: any) => warning.warning.messageType === vmType);
            const warnings = _.chain(warningsByType)
                .sortBy(v => 1 - v.warning.severityRatio)
                .take(4)
                .map(v => this._getShortTextForWarning(v.warning, v.inventoryNumberWithoutPrefix))
                .value();

            const warningsInfoByType: VehicleWarningByType = {
                totalWarnings: warningsByType?.length,
                warningsText: warnings,
                warningType: vmType
            };

            result.push(warningsInfoByType);
        });

        // Get all the other warnings in a own list
        const otherWarnings = _.filter(processedVehicles, (warning) =>
            !_.includes(VehicleWarningConfig.warningsWithOwnCategories || [], warning.warning.messageType));
        const otherWarningsFiltered = _.chain(otherWarnings)
            .sortBy(v => 1 - v.warning.severityRatio)
            .take(4)
            .map(v => this._getShortTextForWarning(v.warning, v.inventoryNumberWithoutPrefix))
            .value();
        const otherWarningsInfo: VehicleWarningByType = {
            totalWarnings: otherWarnings?.length,
            warningsText: otherWarningsFiltered,
            warningType: WarningMessageType.Other
        };
        result.push(otherWarningsInfo);

        this._processedWarnings$.next(result);
    }

    private _sendFeatures() {
        let vehicles: Vehicle[];
        if (!super.stateSnapshot.vehicles) {
            vehicles = [];
        } else {
            vehicles = Array.from(super.stateSnapshot.vehicles.values());
        }

        // find which vehicles that are selected
        const selectedIds = this.stateSnapshot.selectedVehicles?.allValues || [];
        const featuresToDraw: Feature[] = _.chain(vehicles)
            .filter(vehicle => !!vehicle.position)
            .filter((vehicle: Vehicle) => {
                if (this._inventoryFilter?.length > 0) {
                    return _.includes(this._inventoryFilter, vehicle.inventoryNumber);
                }
                return true;
            })
            .map((vehicle: Vehicle) => {
                const isSelected = selectedIds.includes(vehicle.inventoryNumber);

                return this._createVehicleFeature(vehicle, isSelected);
            }).value();

        this._processedFeatures$.next(featuresToDraw);
    }

    private _createVehicleFeature(vehicle: Vehicle, isSelected: boolean) {
        const feature = vehicle.toFeature(isSelected);

        const departureSeconds = vehicle.departureDeviation?.deviation?.getTotalSeconds();
        const arrivalSeconds = vehicle.arrivalDeviation?.deviation?.getTotalSeconds();
        const config = this._appState.stateSnapshot.punctualityConfig;
        const onTomLayOvr = vehicle.onTomLayOvr;
        const cfg = config.getConfig(departureSeconds, arrivalSeconds, onTomLayOvr);

        feature.properties['zIndex'] = 5;
        if (departureSeconds > 0 || arrivalSeconds > 0) {
            // put early icons on top
            feature.properties['zIndex'] = 10;
        }
        if (feature.properties.isSelected === true) {
            feature.properties['zIndex'] = 15;
        }

        feature.properties['deviation-color'] = cfg.color;
        feature.properties['icon'] = cfg.icon;

        // warnings
        feature.properties['hasWarning'] = this._hasVisibleWarning(vehicle.displayableWarnings);
        return feature;
    }

    // checks if warning type is hidden in user settings
    private _hasVisibleWarning(warnings: VehicleWarning[]): boolean {
        if (!warnings || warnings.length === 0) {
            return false;
        }

        let hasVisibleWarning = false;
        _.each(warnings, warning => {
            if (this._appState.stateSnapshot.warningConfig.isVisible(warning.messageType)) {
                hasVisibleWarning = true;
                return;
            }
        });
        return hasVisibleWarning;
    }

    private _setDepartureDeviationProperties(vehicle: Vehicle) {
        const config = this._appState.stateSnapshot.punctualityConfig;
        if (vehicle.departureDeviation?.deviation) {
            const seconds = vehicle.departureDeviation.deviation.getTotalSeconds();
            const values = config.getConfig(seconds);
            vehicle.departureDeviation.color = values.color;
            vehicle.departureDeviation.text = values.text;
        }
        if (vehicle.arrivalDeviation?.deviation) {
            const arrivalSeconds = vehicle.arrivalDeviation.deviation.getTotalSeconds();
            const values = config.getArrivalConfig(arrivalSeconds);
            vehicle.arrivalDeviation.color = values.color;
            vehicle.arrivalDeviation.text = values.text;
        }
    }

    private _getShortTextForWarning(warning: VehicleWarning, inventoryNumberWithoutPrefix: string) {
        switch (warning.messageType) {
            case WarningMessageType.SocWarning:
                return inventoryNumberWithoutPrefix + '&nbsp&nbsp' + warning?.metadata['sensorValue'] + '%';
            case WarningMessageType.VehicleDelay:
                return inventoryNumberWithoutPrefix;
            default:
                return inventoryNumberWithoutPrefix;
        }
    }
}
